import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { useConnection } from 'hooks/useConnection'
import { useReadContractMultiple } from 'hooks/useReadContractMultiple'
import { useMemo } from 'react'
import { Abi, Address, getAddress } from 'viem'

export interface VaultUnderlyingData {
  address: Address
  token0Amount: string
  token1Amount: string
  totalSupply: string
}

interface UseVaultUnderlyingDataConfig {
  totalSupply: {
    abi: Abi
  }
  balances: {
    functionName: 'getUnderlyingBalances' | 'getVaultAmounts'
    abi?: Abi
    address?: Address
  }
  enabled?: boolean
  logFailures?: boolean
}

export function useVaultUnderlyingData(addresses: Address[], config: UseVaultUnderlyingDataConfig) {
  const { isConnected } = useConnection()
  const { totalSupply, balances, logFailures } = config
  const effectiveBalancesAbi = balances.abi ?? totalSupply.abi

  // Create contracts for multicall - both underlying function and totalSupply
  const contracts = useMemo(() => {
    const normalizedVaultAddresses = addresses.map((address) => getAddress(address))
    return normalizedVaultAddresses.flatMap((vaultAddress) => [
      {
        address: balances.address ?? vaultAddress,
        abi: effectiveBalancesAbi,
        functionName: balances.functionName,
        args: balances.functionName === 'getVaultAmounts' ? [vaultAddress] : undefined,
      },
      {
        address: vaultAddress,
        abi: totalSupply.abi,
        functionName: 'totalSupply',
      },
    ])
  }, [addresses, totalSupply.abi, effectiveBalancesAbi, balances.functionName, balances.address])

  const {
    data: response,
    isLoading,
    error,
  } = useReadContractMultiple({
    contracts,
    allowFailure: true,
    query: {
      ...PORTFOLIO_REFETCH_POLICY,
      enabled: isConnected && addresses.length > 0 && config.enabled !== false,
    },
  })

  // Parse the multicall results with proper error handling
  const data = useMemo(() => {
    if (!response) return {}

    return addresses.reduce((acc, address, index) => {
      const baseIndex = index * 2
      const underlyingResult = response[baseIndex]
      const totalSupplyResult = response[baseIndex + 1]

      // Check if both calls succeeded
      if (underlyingResult?.status === 'success' && totalSupplyResult?.status === 'success') {
        const [token0Amount, token1Amount] = underlyingResult.result as readonly [bigint, bigint]
        const totalSupply = totalSupplyResult.result as bigint

        acc[address] = {
          address,
          token0Amount: token0Amount.toString(),
          token1Amount: token1Amount.toString(),
          totalSupply: totalSupply.toString(),
        }
      } else {
        acc[address] = {
          address,
          token0Amount: '0',
          token1Amount: '0',
          totalSupply: '0',
        }
        // Log errors for debugging but don't break the batch
        if (logFailures) {
          const target = balances.functionName
          if (underlyingResult?.status === 'failure') {
            console.warn(`Failed to fetch underlying balances for ${target} ${address}:`, underlyingResult.error)
          }
          if (totalSupplyResult?.status === 'failure') {
            console.warn(`Failed to fetch total supply for ${target} ${address}:`, totalSupplyResult.error)
          }
        }
      }

      return acc
    }, {} as Record<Address, VaultUnderlyingData>)
  }, [addresses, response, logFailures, balances.functionName])

  return {
    data,
    isLoading,
    error,
  }
}
